import { useEffect, useState } from "react";
import { CarrotIcon } from "@/components/CarrotIcon";
import { OrganicUnderline } from "@/components/OrganicLines";
import { SIGNUP_URL } from "@/lib/site";

/**
 * One stat of the hero's import moment. Counts 0 -> value once, shortly after
 * the floating input cards land on the app window, with an ease-out so the
 * "resolved facts" arrive fast and settle gently. SSR and reduced-motion users
 * see the final number; time-based interval because rAF pauses when hidden.
 */
function StatCount({ value, delay }: { value: number; delay: number }) {
  const [n, setN] = useState(value);
  // Gate against the SSR flash (final value -> 0 -> count). The number stays
  // hidden (html.js .count-fx) until the count starts; "armed" marks JS alive
  // so the CSS failsafe stands down. See count-fx rules in index.css.
  const [phase, setPhase] = useState<"idle" | "armed" | "live">("idle");

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setPhase("live"); // no counting: show the final value immediately
      return;
    }
    setPhase("armed");
    let timer: ReturnType<typeof setInterval> | undefined;
    const start = setTimeout(() => {
      const t0 = performance.now();
      const DURATION = 900;
      setN(0);
      setPhase("live");
      timer = setInterval(() => {
        const p = Math.min(1, (performance.now() - t0) / DURATION);
        setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
        if (p >= 1) clearInterval(timer);
      }, 32);
    }, delay);
    return () => {
      clearTimeout(start);
      clearInterval(timer);
    };
  }, [value, delay]);

  return (
    <span
      className={`count-fx tabular-nums ${phase === "armed" ? "count-armed" : ""} ${phase === "live" ? "count-live" : ""}`}
    >
      {n}
    </span>
  );
}

/**
 * Section 1 of the descent: above ground. A conventional split hero on cream,
 * clear in five seconds. The soil metaphor deliberately has not started yet,
 * Orange is limited to the hand-drawn underline and the primary action; the
 * product field itself stays cream and green.
 */
export function Hero() {
  return (
    <section id="top" className="hero-field relative overflow-hidden">
      <div aria-hidden className="hero-ghost">THE RECORD KEEPS ITSELF</div>
      <div className="relative mx-auto max-w-7xl px-5 pb-24 pt-16 sm:px-8 sm:pb-32 sm:pt-24">
        <div className="grid items-center gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          {/* copy */}
          <div className="relative z-10">
            <p
              className="coth-enter mb-6 flex items-center gap-3 text-[0.7rem] font-bold uppercase tracking-[0.22em] text-field-deep/70"
              style={{ animationDelay: "0ms" }}
            >
              <span className="h-px w-8 bg-carrot" />
              AI-native operating memory for horse businesses
            </p>
            <h1
              className="coth-enter max-w-[11ch] text-[clamp(3.25rem,7.4vw,6.8rem)] font-extrabold leading-[0.89] tracking-[-0.055em] text-ink"
              style={{ animationDelay: "70ms" }}
            >
              Ranch chaos, turned into{" "}
              <span className="relative inline-block text-field">
                horse intelligence.
                <OrganicUnderline className="organic-underline-load absolute -bottom-[0.12em] left-0 h-[0.2em] w-full text-carrot" />
              </span>
            </h1>

            <p
              className="coth-enter mt-8 max-w-[34rem] text-lg leading-relaxed text-ink/68 sm:text-xl"
              style={{ animationDelay: "170ms" }}
            >
              Your barn already produces the record: vet PDFs, spreadsheets, photos, texts and spoken
              updates. CarrotOnTheHorse reads the mess and builds the memory behind the work.
            </p>

            <div
              className="coth-enter mt-10 flex flex-col gap-3 sm:flex-row sm:items-center"
              style={{ animationDelay: "260ms" }}
            >
              <a
                href={SIGNUP_URL}
                className="action-button group inline-flex items-center justify-between gap-5 rounded-full py-2 pl-6 pr-2 text-base font-bold"
              >
                Start with what you have
                <span className="flex size-10 items-center justify-center rounded-full bg-ink/10">
                  <CarrotIcon
                    className="h-6 w-5 transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:translate-x-0.5"
                  />
                </span>
              </a>
              <a
                href="/#root-system"
                className="inline-flex items-center justify-center px-5 py-3 text-base font-semibold text-ink/75 underline decoration-carrot/40 decoration-2 underline-offset-8 transition-colors duration-300 hover:text-field-deep hover:decoration-carrot"
              >
                See what lives underneath
              </a>
            </div>
          </div>

          {/* A working product surrounded by the unstructured inputs it understands. */}
          <div className="coth-enter relative lg:translate-x-5" style={{ animationDelay: "150ms" }}>
            <div className="hero-card-l absolute -left-4 top-8 z-20 -rotate-3 rounded-2xl bg-paper px-4 py-3 shadow-[0_22px_55px_-25px_rgba(65,42,17,0.55)] ring-1 ring-ink/8 sm:-left-10">
              <span className="block text-[0.62rem] font-bold uppercase tracking-[0.18em] text-ink/65">Incoming</span>
              <span className="mt-1 block text-sm font-semibold text-ink">GVHS_registry.pdf</span>
            </div>
            <div className="hero-card-r absolute right-0 top-[42%] z-20 rotate-2 rounded-2xl bg-field-deep px-4 py-3 text-cream shadow-[0_22px_55px_-25px_rgba(27,58,47,0.8)] sm:-right-2">
              <span className="block text-[0.62rem] font-bold uppercase tracking-[0.18em] text-cream/75">Voice note · 0:18</span>
              <span className="mt-1 block max-w-48 text-sm font-medium">“Farrier didn’t finish Domino.”</span>
            </div>
            <div className="product-bezel relative mx-auto max-w-[46rem]">
              <div className="product-bezel-core overflow-hidden">
                <div className="flex items-center justify-between border-b border-ink/8 bg-paper/95 px-4 py-3">
                  <div className="flex gap-1.5" aria-hidden>
                    <span className="size-2 rounded-full bg-carrot/70" />
                    <span className="size-2 rounded-full bg-field/35" />
                    <span className="size-2 rounded-full bg-ink/15" />
                  </div>
                  <span className="text-[0.63rem] font-bold uppercase tracking-[0.18em] text-ink/65">Live barn memory</span>
                </div>
                <img
                  src="/static/ai-screen.png"
                  alt="A chat with the CarrotOnTheHorse barn assistant confirming the record for a newborn foal"
                  width={3840}
                  height={2160}
                  fetchPriority="high"
                  decoding="async"
                  className="block w-full"
                />
              </div>
            </div>
            <div className="relative z-20 mx-auto -mt-5 grid max-w-[88%] grid-cols-3 overflow-hidden rounded-2xl bg-paper shadow-[0_26px_60px_-30px_rgba(55,36,17,0.55)] ring-1 ring-ink/8">
              {(
                [
                  [47, "horses resolved"],
                  [5, "duplicates flagged"],
                  [3, "care gaps found"],
                ] as const
              ).map(([value, label], i) => (
                <div key={label} className={`px-3 py-4 text-center ${i ? "border-l border-ink/8" : ""}`}>
                  <strong className="block font-display text-xl text-field-deep sm:text-2xl">
                    <StatCount value={value} delay={1000 + i * 120} />
                  </strong>
                  <span className="mt-0.5 block text-[0.65rem] font-semibold leading-tight text-ink/70 sm:text-xs">{label}</span>
                </div>
              ))}
            </div>
            <p className="mt-5 text-center text-xs text-ink/65">Illustrative registry import · working demo flow</p>
          </div>
        </div>

        <a
          href="/#problem"
          className="coth-enter mt-20 flex items-center justify-center gap-4 text-xs font-bold uppercase tracking-[0.2em] text-field-deep/80 sm:mt-28"
          style={{ animationDelay: "380ms" }}
        >
          <span className="h-px w-12 bg-field/25" />
          What you see is only the surface
          <span className="inline-flex size-8 items-center justify-center rounded-full bg-field-soft">
            <CarrotIcon direction="down" className="h-[1.125rem] w-3.5" />
          </span>
          <span className="h-px w-12 bg-field/25" />
        </a>
      </div>
    </section>
  );
}
